import React from 'react'
import { Value } from 'slate'
import classNames from 'classnames'
import { substring } from 'fbjs/lib/UnicodeUtils'

import CommentsEditor from 'components/Editors/CommentEditor'
import {
  convertValueToEntities,
  convertValueToText,
} from 'components/Editors/CommentEditor/utils'

export const MAX_COMMENT_CHARACTERS = 120

const getEditorValueFromProps = ({ comment, truncated = false }) => {
  const commentText =
    comment.content.length > MAX_COMMENT_CHARACTERS && truncated
      ? `${comment.content.slice(0, MAX_COMMENT_CHARACTERS)}...`
      : comment.content

  const commentEntities = comment.entities ? comment.entities.toJS() : []

  function createTextNode(text) {
    return {
      object: 'text',
      text,
      marks: [],
    }
  }

  function createInlineNode({ text, type, data }) {
    return {
      object: 'inline',
      type,
      data,
      nodes: [{ object: 'text', text, marks: [] }],
    }
  }

  const { fragments, offset } = commentEntities
    .filter(entity => ['MENTION', 'NOTELINK'].includes(entity.type))
    .sort((entityA, entityB) => entityA.startOffset - entityB.startOffset)
    .reduce(
      ({ fragments, offset }, entity) => {
        const { startOffset, endOffset, type, data } = entity
        if (startOffset > offset) {
          fragments.push(
            createTextNode(substring(commentText, offset, startOffset))
          )
        }
        fragments.push(
          createInlineNode({
            type,
            data,
            text: substring(commentText, startOffset, endOffset + 1),
          })
        )

        return { fragments, offset: endOffset + 1 }
      },
      { fragments: [], offset: 0 }
    )

  fragments.push(createTextNode(substring(commentText, offset, Infinity)))

  const json = {
    object: 'value',
    document: {
      object: 'document',
      data: {},
      nodes: [
        {
          object: 'block',
          type: 'line',
          data: {},
          nodes: fragments,
        },
      ],
    },
  }

  return Value.fromJSON(json)
}

function CommentsContent(
  {
    onStopEditing,
    showActivity = false,
    updatingComment = false,
    truncated = false,
    comment,
    updateComment,
  },
  ref
) {
  const wrapperRef = React.useRef(null)
  const [value, setValue] = React.useState(() =>
    getEditorValueFromProps({ comment, truncated })
  )

  function onChange(content, value) {
    setValue(value)
  }

  function onSubmit() {
    const entities = convertValueToEntities(value)
    const content = convertValueToText(value)
    if (content) {
      updateComment({
        input: {
          commentId: comment.id,
          content,
          entities,
        },
      })
    }
    onStopEditing()
  }

  React.useEffect(
    function onUpdate() {
      setValue(getEditorValueFromProps({ comment, truncated }))
    },
    [comment, truncated]
  )

  return (
    <div
      ref={wrapperRef}
      className={classNames(
        'c-editor comment-editor line-height-large shade-05',
        { bold: showActivity }
      )}
    >
      <CommentsEditor
        ref={ref}
        autoFocus={false}
        value={value}
        onChange={onChange}
        readOnly={!updatingComment}
        onSubmit={onSubmit}
        onCancel={onStopEditing}
        editorClassName="global-comment-editor line-height-larger"
        placeholder=""
      />
    </div>
  )
}

export default React.forwardRef(CommentsContent)
